import * as signalR from '@microsoft/signalr';
import type { ChatMessage, ConversationStatus } from './types';

type TokenChunkHandler = (convId: string, text: string) => void;
type MessageCompleteHandler = (convId: string, message: ChatMessage) => void;
type StatusChangedHandler = (convId: string, status: ConversationStatus) => void;
type AgentJoinedHandler = (convId: string, agentName: string) => void;
type InboxUpdateHandler = (data: { id: string; status: string; title: string | null; lastMessageAt: string }) => void;

class ChatHubClient {
  private connection: signalR.HubConnection | null = null;
  private tokenFactory: (() => string | null) | null = null;
  private apiBase = '';

  private tokenChunkHandlers = new Set<TokenChunkHandler>();
  private messageCompleteHandlers = new Set<MessageCompleteHandler>();
  private statusChangedHandlers = new Set<StatusChangedHandler>();
  private agentJoinedHandlers = new Set<AgentJoinedHandler>();
  private inboxUpdateHandlers = new Set<InboxUpdateHandler>();

  // Tracked so we can re-invoke server-side joins automatically after reconnect —
  // SignalR groups are connection-scoped and lost on disconnect.
  private joinedConversations = new Map<string, string | null>(); // convId -> anonymousId
  private inboxJoined = false;
  private inboxOrgHint: string | null = null;

  configure(apiBase: string, getToken: () => string | null) {
    this.apiBase = apiBase;
    this.tokenFactory = getToken;
  }

  async connect() {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;

    // When apiBase is empty (web app widget), fall back to VITE_CHAT_HUB_URL so the
    // WebSocket upgrade goes directly to the API — Next.js rewrites don't proxy WS upgrades.
    const base = this.apiBase || import.meta.env.VITE_CHAT_HUB_URL || '';
    const hubUrl = `${base}/chat-hub`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.tokenFactory?.() ?? '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => {
          const schedule = [0, 2000, 5000, 15000, 30000, 60000, 120000];
          return schedule[Math.min(ctx.previousRetryCount, schedule.length - 1)];
        },
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.connection.onreconnected(() => this.rejoinGroups());

    this.connection.on('TokenChunk', ({ convId, text }: { convId: string; text: string }) => {
      this.tokenChunkHandlers.forEach(h => h(convId, text));
    });

    this.connection.on('MessageComplete', ({ convId, message }: { convId: string; message: ChatMessage }) => {
      this.messageCompleteHandlers.forEach(h => h(convId, message));
    });

    this.connection.on('StatusChanged', ({ convId, status }: { convId: string; status: ConversationStatus }) => {
      this.statusChangedHandlers.forEach(h => h(convId, status));
    });

    this.connection.on('AgentJoined', ({ convId, agentName }: { convId: string; agentName: string }) => {
      this.agentJoinedHandlers.forEach(h => h(convId, agentName));
    });

    this.connection.on('InboxUpdate', (data: { id: string; status: string; title: string | null; lastMessageAt: string }) => {
      this.inboxUpdateHandlers.forEach(h => h(data));
    });

    try {
      await this.connection.start();
      // Rejoin groups that were requested before connection was ready
      this.rejoinGroups();
    } catch {
      // Swallow — will reconnect automatically
    }
  }

  async joinConversation(conversationId: string, anonymousId?: string) {
    const anonId = anonymousId ?? null;
    this.joinedConversations.set(conversationId, anonId);
    if (this.connection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('JoinConversation', conversationId, anonId);
    } catch { /* ignore */ }
  }

  leaveConversation(conversationId: string) {
    this.joinedConversations.delete(conversationId);
  }

  async joinInbox(orgId?: string | null) {
    this.inboxJoined = true;
    this.inboxOrgHint = orgId ?? null;
    if (this.connection?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('JoinInbox', this.inboxOrgHint);
    } catch { /* ignore */ }
  }

  leaveInbox() {
    this.inboxJoined = false;
    this.inboxOrgHint = null;
  }

  async disconnect() {
    await this.connection?.stop();
    this.connection = null;
    this.joinedConversations.clear();
    this.inboxJoined = false;
    this.inboxOrgHint = null;
  }

  private rejoinGroups() {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
    for (const [convId, anonId] of this.joinedConversations) {
      this.connection.invoke('JoinConversation', convId, anonId).catch(() => {});
    }
    if (this.inboxJoined) {
      this.connection.invoke('JoinInbox', this.inboxOrgHint).catch(() => {});
    }
  }

  onTokenChunk(handler: TokenChunkHandler) {
    this.tokenChunkHandlers.add(handler);
    return () => this.tokenChunkHandlers.delete(handler);
  }

  onMessageComplete(handler: MessageCompleteHandler) {
    this.messageCompleteHandlers.add(handler);
    return () => this.messageCompleteHandlers.delete(handler);
  }

  onStatusChanged(handler: StatusChangedHandler) {
    this.statusChangedHandlers.add(handler);
    return () => this.statusChangedHandlers.delete(handler);
  }

  onAgentJoined(handler: AgentJoinedHandler) {
    this.agentJoinedHandlers.add(handler);
    return () => this.agentJoinedHandlers.delete(handler);
  }

  onInboxUpdate(handler: InboxUpdateHandler) {
    this.inboxUpdateHandlers.add(handler);
    return () => this.inboxUpdateHandlers.delete(handler);
  }
}

// Module-level singleton — one connection shared across all widget instances
export const chatHub = new ChatHubClient();
